import { useEffect } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createHeadingNode, $createQuoteNode, HeadingNode, QuoteNode } from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import { ListItemNode, ListNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from "@lexical/list";
import { AutoLinkNode, LinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $generateHtmlFromNodes } from "@lexical/html";
import { $getSelection, $isRangeSelection, FORMAT_TEXT_COMMAND, REDO_COMMAND, UNDO_COMMAND } from "lexical";
import { Bold, Italic, Underline, List, ListOrdered, Quote, Link as LinkIcon, Undo2, Redo2 } from "lucide-react";

const theme = {
  paragraph: "mb-3 text-sm leading-7 text-dir-text",
  heading: { h1: "mb-4 text-3xl font-bold text-dir-text", h2: "mb-3 text-2xl font-bold text-dir-text", h3: "mb-3 text-xl font-semibold text-dir-text" },
  quote: "my-4 border-l-2 border-dir-gold pl-4 italic text-dir-muted",
  list: { ul: "my-3 list-disc pl-6 text-dir-text", ol: "my-3 list-decimal pl-6 text-dir-text" },
  link: "text-dir-gold underline",
  text: { bold: "font-bold", italic: "italic", underline: "underline" },
};

function Toolbar() {
  const [editor] = useLexicalComposerContext();
  const button = "rounded-md p-2 text-dir-muted hover:bg-dir-gold/10 hover:text-dir-gold";
  const link = () => {
    const url = window.prompt("Link URL");
    if (url !== null) editor.dispatchCommand(TOGGLE_LINK_COMMAND, url || null);
  };
  const block = (kind) => editor.update(() => { const selection = $getSelection(); if ($isRangeSelection(selection)) $setBlocksType(selection, () => kind === "quote" ? $createQuoteNode() : $createHeadingNode(kind)); });
  return (
    <div className="flex flex-wrap gap-1 border-b border-dir-gold/15 bg-dir-deep/70 p-2">
      <button type="button" className={button} title="Bold" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}><Bold className="h-4 w-4" /></button>
      <button type="button" className={button} title="Italic" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}><Italic className="h-4 w-4" /></button>
      <button type="button" className={button} title="Underline" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")}><Underline className="h-4 w-4" /></button>
      <button type="button" className={`${button} text-xs font-bold`} title="Heading 2" onClick={() => block("h2")}>H2</button>
      <button type="button" className={button} title="Bulleted list" onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND)}><List className="h-4 w-4" /></button>
      <button type="button" className={button} title="Numbered list" onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND)}><ListOrdered className="h-4 w-4" /></button>
      <button type="button" className={button} title="Quote" onClick={() => block("quote")}><Quote className="h-4 w-4" /></button>
      <button type="button" className={button} title="Link" onClick={link}><LinkIcon className="h-4 w-4" /></button>
      <span className="mx-1 w-px bg-dir-gold/15" />
      <button type="button" className={button} title="Undo" onClick={() => editor.dispatchCommand(UNDO_COMMAND)}><Undo2 className="h-4 w-4" /></button>
      <button type="button" className={button} title="Redo" onClick={() => editor.dispatchCommand(REDO_COMMAND)}><Redo2 className="h-4 w-4" /></button>
    </div>
  );
}

function LoadState({ value }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    if (!value?.state) return;
    try { editor.setEditorState(editor.parseEditorState(value.state)); } catch { /* retain empty editor */ }
  }, [editor, value?.state]);
  return null;
}

export default function RichTextEditor({ value, onChange }) {
  const config = { namespace: "DirNationArticleEditor", theme, nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, AutoLinkNode], onError: (error) => { throw error; } };
  return (
    <LexicalComposer initialConfig={config}>
      <div className="overflow-hidden rounded-xl border border-dir-gold/20 bg-dir-elevated" data-testid="rich-text-editor">
        <Toolbar />
        <div className="relative min-h-72">
          <RichTextPlugin contentEditable={<ContentEditable className="min-h-72 px-5 py-4 outline-none" />} placeholder={<div className="pointer-events-none absolute left-5 top-4 text-sm text-dir-faint">Write your story…</div>} ErrorBoundary={LexicalErrorBoundary} />
          <HistoryPlugin /><ListPlugin /><LinkPlugin /><LoadState value={value} />
          <OnChangePlugin onChange={(editorState, editor) => {
            editorState.read(() => onChange({ state: JSON.stringify(editorState.toJSON()), html: $generateHtmlFromNodes(editor) }));
          }} />
        </div>
      </div>
    </LexicalComposer>
  );
}
